import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { feedbackService } from "@/services/feedbackService";

//  Lấy danh sách feedback của Tour
export const fetchFeedbackByTourId = createAsyncThunk(
  "feedback/fetchByTourId",
  async (tourId, { rejectWithValue }) => {
    try {
      const response = await feedbackService.getByTourId(tourId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching feedbacks"
      );
    }
  }
);

export const fetchFeedbackCountByTour = createAsyncThunk(
  "feedbacks/fetchCountByTour",
  async (tourId, { rejectWithValue }) => {
    try {
      const response = await feedbackService.getFeedbackCountByTour(tourId);
      return response.data.count; // Lấy count từ response
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

//  Gửi feedback mới hoặc reply (nếu có parentFeedbackId)
export const createOrReplyFeedback = createAsyncThunk(
  "feedback/createOrReply",
  async (feedbackData, { rejectWithValue }) => {
    try {
      const response = await feedbackService.create(feedbackData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error creating feedback");
    }
  }
);

export const updateFeedback = createAsyncThunk(
  "feedback/updateFeedback",
  async (feedbackData, { rejectWithValue }) => {
    try {
      await feedbackService.updateFeedback(feedbackData);
      return feedbackData;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteFeedback = createAsyncThunk(
  "feedback/deleteFeedback",
  async (feedbackId, { rejectWithValue }) => {
    try {
      const response = await feedbackService.deleteFeedback(feedbackId);
      return response.data; // { message: "Feedback deleted successfully", feedbackId }
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

const initialState = {
  feedbacks: [],
  feedbackCount: 0, // Thêm biến lưu số lượng feedback
  loading: false,
  error: null,
  averageRating: 0,
  ratingStats: {
    5: 0,
    4: 0,
    3: 0,
    2: 0,
    1: 0,
  },
  ratingPercentages: {
    5: 0,
    4: 0,
    3: 0,
    2: 0,
    1: 0,
  },
};

const feedbackSlice = createSlice({
  name: "feedback",
  initialState,
  reducers: {},

  extraReducers: (builder) => {
    builder
      // ✅ Lấy danh sách feedback
      .addCase(fetchFeedbackByTourId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFeedbackByTourId.fulfilled, (state, action) => {
        state.loading = false;

        state.feedbacks = action.payload.feedbacks ?? []; // Cập nhật danh sách feedback

        state.averageRating = feedbackService.calculateAverageRating(
          state.feedbacks
        );

        const { stats, percentages } =
          feedbackService.calculateRatingStatistics(state.feedbacks);
        state.ratingStats = stats;
        state.ratingPercentages = percentages;
      })
      .addCase(fetchFeedbackByTourId.rejected, (state, action) => {
        state.loading = false;

        state.error = action.payload;
      })

      // ✅ Tạo feedback hoặc reply
      .addCase(createOrReplyFeedback.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOrReplyFeedback.fulfilled, (state, action) => {
        state.loading = false;
        const newFeedback = action.payload.feedback;

        if (newFeedback.parentFeedbackId) {
          // Nếu là reply, tìm feedback cha
          const parentFeedback = state.feedbacks.find(
            (f) => f.feedbackId === newFeedback.parentFeedbackId
          );

          if (parentFeedback) {
            // Nếu tìm thấy feedback cha, thêm vào replies của nó
            parentFeedback.replies = [...parentFeedback.replies, newFeedback];
          }
        } else {
          // Nếu không phải reply, thêm vào danh sách feedback chính
          state.feedbacks.push(newFeedback);
        }
        state.feedbackCount += 1;

        state.averageRating = feedbackService.calculateAverageRating(
          state.feedbacks
        );

        const { stats, percentages } =
          feedbackService.calculateRatingStatistics(state.feedbacks);
        state.ratingStats = stats;
        state.ratingPercentages = percentages;
      })
      .addCase(createOrReplyFeedback.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Lấy số lượng feedback
      .addCase(fetchFeedbackCountByTour.fulfilled, (state, action) => {
        state.feedbackCount = action.payload; // Cập nhật count
      })

      //Cập nhật feedback
      .addCase(updateFeedback.fulfilled, (state, action) => {
        state.loading = false;

        const updateFeedback = action.payload;

        // Tìm feedback chính trong danh sách
        const parentFeedbackIndex = state.feedbacks.findIndex(
          (fb) => fb.feedbackId === updateFeedback.feedbackId
        );

        if (parentFeedbackIndex !== -1) {
          // Nếu feedback chính được cập nhật, thay thế nó
          state.feedbacks[parentFeedbackIndex] = {
            ...state.feedbacks[parentFeedbackIndex],
            ...updateFeedback,
          };
        } else {
          // Nếu feedback là reply, tìm trong replies
          state.feedbacks.forEach((feedback) => {
            if (feedback.replies) {
              const replyIndex = feedback.replies.findIndex(
                (reply) => reply.feedbackId === updateFeedback.feedbackId
              );

              if (replyIndex !== -1) {
                feedback.replies[replyIndex] = {
                  ...feedback.replies[replyIndex],
                  ...updateFeedback,
                };
              }
            }
          });
        }
        state.averageRating = feedbackService.calculateAverageRating(
          state.feedbacks
        );

        const { stats, percentages } =
          feedbackService.calculateRatingStatistics(state.feedbacks);
        state.ratingStats = stats;
        state.ratingPercentages = percentages;
      })

      .addCase(updateFeedback.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Xoá Feedback
      .addCase(deleteFeedback.fulfilled, (state, action) => {
        state.loading = false;

        const feedbackIdToDelete = action.payload.feedbackId;

        // Xóa nếu feedback chính
        state.feedbacks = state.feedbacks.filter(
          (fb) => fb.feedbackId !== feedbackIdToDelete
        );

        // Xóa nếu feedback là reply
        state.feedbacks.forEach((fb) => {
          if (fb.replies) {
            fb.replies = fb.replies.filter(
              (reply) => reply.feedbackId !== feedbackIdToDelete
            );
          }
        });
        state.feedbackCount = action.payload.count;

        state.averageRating = feedbackService.calculateAverageRating(
          state.feedbacks
        );

        const { stats, percentages } =
          feedbackService.calculateRatingStatistics(state.feedbacks);
        state.ratingStats = stats;
        state.ratingPercentages = percentages;
      });
  },
});

export default feedbackSlice.reducer;
